/**
 * snapshot-seq-store · captures the REST snapshot's `lastSeq` watermark.
 *
 * Every market read response carries `{ data, lastSeq: { sequenceId } }`
 * (see `backend/backend-api-service/src/realtime/envelope.ts`). The
 * generated query functions parse only `data` — this store lets them
 * stash the `lastSeq.sequenceId` (base64 of the 15-byte chain cursor)
 * keyed by the query hash, so the realtime hook can seed its sequence
 * gate once the snapshot lands: every NATS delta whose `sequenceId` is
 * `<=` the snapshot watermark is then dropped as already-reflected (the
 * Binance "discard events already in the snapshot" step).
 *
 * Platform-agnostic (SDK core, web + RN): a module-level `Map`, no
 * platform APIs — mirrors `last-seq-store.ts` / `seq-gate-store.ts`.
 */

import { hashKey, type QueryKey } from '@tanstack/react-query';

/** query hash → base64 `lastSeq.sequenceId` from the most recent fetch. */
const store = new Map<string, string>();

/** Bound the map — one entry per realtime query key. */
const MAX_ENTRIES = 4096;

/**
 * Record a fetch response's `lastSeq` watermark. Called by every generated
 * query function after it parses the JSON envelope. A no-op when the
 * response carries no `lastSeq` (non-realtime endpoints) — so it is safe
 * to call unconditionally from the shared queryFn template.
 */
export function recordSnapshotLastSeq(queryKey: QueryKey, json: unknown): void {
  if (json === null || typeof json !== 'object') return;
  const lastSeq = (json as { lastSeq?: unknown }).lastSeq;
  if (lastSeq === null || typeof lastSeq !== 'object') return;
  const sequenceId = (lastSeq as { sequenceId?: unknown }).sequenceId;
  const key = hashKey(queryKey);
  if (typeof sequenceId !== 'string' || sequenceId.length === 0) {
    store.delete(key);
    return;
  }
  store.delete(key);
  store.set(key, sequenceId);
  if (store.size > MAX_ENTRIES) {
    const oldest = store.keys().next().value;
    if (oldest !== undefined) store.delete(oldest);
  }
}

/** Read the base64 watermark recorded for a query hash, if any. */
export function readSnapshotLastSeq(queryHash: string): string | undefined {
  return store.get(queryHash);
}

/** Drop everything — hooked by the logout-cache-clear path. */
export function clearAllSnapshotSeqs(): void {
  store.clear();
}
